import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue';
import { useQuery, type UseQueryReturnType } from '@tanstack/vue-query';
import { useAuthStore } from '@/stores/auth';
import { usePayRepo } from '../data/provider';
import type { Payroll } from '../data/types';

/**
 * Schedule filter values, mirroring the prototype's #scheduleFilters chips
 * (Optimal-HR-Payroll-Module.html, line 1101+). The filter is single-select.
 */
export type ScheduleFilter = 'all' | 'upcoming' | 'in-progress' | 'completed' | 'off-cycle';

/**
 * Group key used for counts shown on the filter chips and as the
 * implicit bucket each payroll falls into.
 */
export type ScheduleGroupKey = 'upcoming' | 'inProgress' | 'offCycle' | 'completed';

export interface ScheduleCounts {
  all: number;
  upcoming: number;
  inProgress: number;
  offCycle: number;
  completed: number;
}

/**
 * Returns the bucket a payroll belongs to.
 *   - IN_PROGRESS  → inProgress      (regardless of OffCycle)
 *   - OffCycle:true (else)            → offCycle
 *   - PENDING (else)                  → upcoming
 *   - COMPLETED (else)                → completed
 *   - SKIPPED                         → null (hidden from all buckets)
 */
export function bucketOf(p: Payroll): ScheduleGroupKey | null {
  if (p.Status === 'IN_PROGRESS') return 'inProgress';
  if (p.OffCycle) return 'offCycle';
  if (p.Status === 'PENDING') return 'upcoming';
  if (p.Status === 'COMPLETED') return 'completed';
  return null;
}

export function countGroups(payrolls: Payroll[] | undefined): ScheduleCounts {
  const counts: ScheduleCounts = {
    all: 0,
    upcoming: 0,
    inProgress: 0,
    offCycle: 0,
    completed: 0,
  };
  if (!payrolls?.length) return counts;
  for (const p of payrolls) {
    const b = bucketOf(p);
    if (b === null) continue;
    counts[b] += 1;
    counts.all += 1;
  }
  return counts;
}

export function applyFilter(payrolls: Payroll[] | undefined, filter: ScheduleFilter): Payroll[] {
  if (!payrolls?.length) return [];
  const visible = payrolls.filter((p) => bucketOf(p) !== null);
  if (filter === 'all') return [...visible];
  if (filter === 'off-cycle') return visible.filter((p) => Boolean(p.OffCycle));
  const targetBucket: ScheduleGroupKey =
    filter === 'in-progress' ? 'inProgress' : (filter as ScheduleGroupKey);
  return visible.filter((p) => bucketOf(p) === targetBucket);
}

/**
 * Group payrolls by their calendar month (e.g. "Jan 2026"), preserving
 * input order. Matches the prototype's `renderScheduleGroups()` behavior:
 * single horizontal row with small month-label dividers between groups.
 */
export interface ScheduleMonthGroup {
  monthKey: string;
  label: string;
  items: Payroll[];
}

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export function monthKeyOf(p: Payroll): string {
  if (p.Year && p.Month) return `${p.Year}-${String(p.Month).padStart(2, '0')}`;
  if (p.PayDate) {
    const [y, m] = p.PayDate.split('-');
    if (y && m) return `${y}-${m}`;
  }
  return '0000-00';
}

export function monthLabelOf(p: Payroll): string {
  if (p.Year && p.Month) {
    const m = MONTH_NAMES[p.Month - 1] ?? '???';
    return `${m} ${p.Year}`;
  }
  if (p.PayDate) {
    const [y, m] = p.PayDate.split('-');
    if (y && m) {
      const idx = Number(m) - 1;
      const name = MONTH_NAMES[idx];
      if (name) return `${name} ${y}`;
    }
  }
  return '—';
}

export function groupByMonth(payrolls: Payroll[]): ScheduleMonthGroup[] {
  const groups: ScheduleMonthGroup[] = [];
  const map = new Map<string, ScheduleMonthGroup>();
  // Sort by month ascending, then PayDate ascending for stability.
  const sorted = [...payrolls].sort((a, b) => {
    const ma = monthKeyOf(a);
    const mb = monthKeyOf(b);
    if (ma !== mb) return ma.localeCompare(mb);
    return (a.PayDate ?? '￿').localeCompare(b.PayDate ?? '￿');
  });
  for (const p of sorted) {
    const key = monthKeyOf(p);
    let g = map.get(key);
    if (!g) {
      g = { monthKey: key, label: monthLabelOf(p), items: [] };
      map.set(key, g);
      groups.push(g);
    }
    g.items.push(p);
  }
  return groups;
}

export interface UsePayrollsOptions {
  /** Year override; defaults to the current year (backend default). */
  year?: number;
  /**
   * Extra gate ANDed with the `employer_id` check. Lets a caller defer the
   * `/payrolls` fetch until it's actually needed — e.g. the top-bar
   * ResumePayrollPill only enables it inside the Payroll module so the list
   * isn't fetched on Home right after sign-in. Defaults to enabled.
   */
  enabled?: MaybeRefOrGetter<boolean>;
}

export interface UsePayrollsReturn {
  query: UseQueryReturnType<Payroll[], Error>;
  counts: ComputedRef<ScheduleCounts>;
}

/**
 * TanStack Query wrapper around `PayRepo.listPayrolls`. The query key includes
 * `employerId` so switching tenants invalidates and refetches naturally.
 * The query stays disabled until an `employer_id` claim is present.
 */
export function usePayrolls(options: UsePayrollsOptions = {}): UsePayrollsReturn {
  const authStore = useAuthStore();
  const repo = usePayRepo();
  const employerId = computed(() => authStore.claims?.employer_id ?? null);
  const year = options.year;

  const query = useQuery<Payroll[], Error>({
    queryKey: ['payroll', 'list', employerId, year],
    queryFn: () => {
      const id = employerId.value;
      if (!id) return Promise.resolve<Payroll[]>([]);
      return repo.listPayrolls(id, year !== undefined ? { year } : {});
    },
    enabled: computed(
      () =>
        Boolean(employerId.value) && (options.enabled === undefined || toValue(options.enabled)),
    ),
    staleTime: 60_000,
  });

  const counts = computed(() => countGroups(query.data.value));

  return { query, counts };
}
